import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, CircularProgress, Avatar, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, List, ListItem, ListItemAvatar, ListItemText, TextField
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, SwapHoriz as SwapHorizIcon, ExpandLess, ExpandMore, AccountTree as AccountTreeIcon, Group as GroupIcon, Person as PersonIcon, SupervisorAccount as SupervisorAccountIcon } from '@mui/icons-material';
import {
  fetchEmployeeManagerMappings,
  fetchUnmappedEmployees,
  fetchUsers,
  createEmployeeManagerMapping,
  releaseEmployeeManagerMapping,
  replaceEmployeeInMapping,
  fetchOrgTree
} from '../services/api';

function ResourceMapping() {
  const [mappings, setMappings] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [users, setUsers] = useState([]);
  const [orgTree, setOrgTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapDialog, setMapDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedManager, setSelectedManager] = useState('');
  const [replaceDialog, setReplaceDialog] = useState(false);
  const [replaceMappingId, setReplaceMappingId] = useState(null);
  const [replaceEmployee, setReplaceEmployee] = useState('');
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => { loadAll(); }, []);
  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [m, u, us, tree] = await Promise.all([
        fetchEmployeeManagerMappings(), fetchUnmappedEmployees(), fetchUsers(), fetchOrgTree()
      ]);
      setMappings(m); setUnmapped(u); setUsers(us); setOrgTree(tree);
    } catch (e) {
      setError('Failed to load data: ' + (e.message || e.toString()));
    }
    setLoading(false);
  };

  // --- Mapping Actions ---
  const openMapDialog = (emp) => { setSelectedEmployee(emp); setSelectedManager(''); setMapDialog(true); };
  const handleMap = async () => {
    if (!selectedManager) return;
    const res = await createEmployeeManagerMapping(selectedEmployee.id, selectedManager);
    if (res.error) setError(res.error); else { setMapDialog(false); loadAll(); }
  };
  const handleRelease = async (id) => { await releaseEmployeeManagerMapping(id); loadAll(); };
  const openReplaceDialog = (id) => { setReplaceMappingId(id); setReplaceEmployee(''); setReplaceDialog(true); };
  const handleReplace = async () => {
    if (!replaceEmployee) return;
    const res = await replaceEmployeeInMapping(replaceMappingId, replaceEmployee);
    if (res.error) setError(res.error); else { setReplaceDialog(false); loadAll(); }
  };

  // --- Org Tree ---
  const renderTree = (node, level = 0) => {
    if (!node) return null;
    const isExpanded = expanded[node.id] || false;
    const hasChildren = node.children && node.children.length > 0;
    const match = search && node.full_name.toLowerCase().includes(search.toLowerCase());
    return (
      <Box key={node.id} sx={{ ml: level * 3, mb: 1, p: 1, borderRadius: 2, bgcolor: match ? 'primary.lighter' : 'background.paper', border: '1px solid #e0e0e0', boxShadow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: node.status === 'inactive' ? 'grey.400' : 'primary.main' }}><PersonIcon /></Avatar>
          <Typography variant="subtitle1" fontWeight={match ? 'bold' : 'normal'} color={node.status === 'inactive' ? 'text.disabled' : 'text.primary'}>
            {node.full_name}
          </Typography>
          <Chip label={node.status} color={node.status === 'inactive' ? 'default' : 'success'} size="small" />
          <Chip label={getRoleName(node.user_role_id)} color="info" size="small" />
          {hasChildren && (
            <IconButton size="small" onClick={() => setExpanded(e => ({ ...e, [node.id]: !isExpanded }))}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
        {hasChildren && isExpanded && node.children.map(child => renderTree(child, level + 1))}
      </Box>
    );
  };
  const getRoleName = (roleId) => {
    const user = users.find(u => u.user_role_id === roleId);
    return user ? user.full_name : 'Role';
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTreeIcon color="primary" fontSize="large" /> Resource Mapping
      </Typography>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      <Grid container spacing={3}>
        {/* Unmapped Employees */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GroupIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Unmapped Employees</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {unmapped.length === 0 ? (
              <Typography color="text.secondary">All employees are mapped.</Typography>
            ) : (
              <List dense>
                {unmapped.map(emp => (
                  <ListItem key={emp.id} secondaryAction={
                    <Tooltip title="Map to Manager">
                      <IconButton edge="end" color="primary" onClick={() => openMapDialog(emp)}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  }>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}><PersonIcon /></Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography fontWeight="bold">{emp.full_name}</Typography>}
                      secondary={<Chip label={emp.department || 'N/A'} size="small" />}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        {/* Current Mappings */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 2, minHeight: 300 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SupervisorAccountIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Current Mappings</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {mappings.length === 0 ? (
              <Typography color="text.secondary">No mappings found.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Manager</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map(m => (
                      <TableRow key={m.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: m.employee_status === 'inactive' ? 'grey.400' : 'primary.main' }}><PersonIcon /></Avatar>
                            <Typography fontWeight="bold">{m.employee_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: m.manager_status === 'inactive' ? 'grey.400' : 'secondary.main' }}><PersonIcon /></Avatar>
                            <Typography>{m.manager_name || 'None'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={m.employee_status} color={m.employee_status === 'inactive' ? 'default' : 'success'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Release Mapping"><IconButton onClick={() => handleRelease(m.id)} color="error"><DeleteIcon /></IconButton></Tooltip>
                          <Tooltip title="Replace Employee"><IconButton onClick={() => openReplaceDialog(m.id)} color="primary"><SwapHorizIcon /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        {/* Org Tree */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccountTreeIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Org Tree</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TextField label="Search" value={search} onChange={e => setSearch(e.target.value)} size="small" sx={{ mb: 2, width: 300 }} />
            {orgTree.length === 0 ? (
              <Typography color="text.secondary">No org tree data.</Typography>
            ) : (
              orgTree.map(root => renderTree(root))
            )}
          </Paper>
        </Grid>
      </Grid>
      {/* Map Dialog */}
      <Dialog open={mapDialog} onClose={() => setMapDialog(false)}>
        <DialogTitle>Map Employee to Manager</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Manager</InputLabel>
            <Select value={selectedManager} onChange={e => setSelectedManager(e.target.value)} label="Manager">
              {users.filter(u => u.status === 'active' && u.id !== selectedEmployee?.id).map(u => (
                <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialog(false)}>Cancel</Button>
          <Button onClick={handleMap} variant="contained">Map</Button>
        </DialogActions>
      </Dialog>
      {/* Replace Dialog */}
      <Dialog open={replaceDialog} onClose={() => setReplaceDialog(false)}>
        <DialogTitle>Replace Employee in Mapping</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Employee</InputLabel>
            <Select value={replaceEmployee} onChange={e => setReplaceEmployee(e.target.value)} label="New Employee">
              {users.filter(u => u.status === 'active').map(u => (
                <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialog(false)}>Cancel</Button>
          <Button onClick={handleReplace} variant="contained">Replace</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ResourceMapping;